import { API_BASE_URL, API_KEY, CHANNELS } from '../config'

/**
 * Shared fetch helper — attaches x-api-key header.
 */
async function request(path, params = {}) {
  const base = API_BASE_URL.startsWith('http') ? API_BASE_URL : window.location.origin
  const url = new URL(`${API_BASE_URL}${path}`, base)
  Object.entries(params).forEach(([k, v]) => {
    if (v != null && v !== '') url.searchParams.set(k, String(v))
  })
  const res = await fetch(url.toString(), {
    headers: {
      'Content-Type': 'application/json',
      ...(API_KEY ? { 'x-api-key': API_KEY } : {})
    }
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(body.message || `API ${res.status}`)
  }
  return res.json()
}

// ── Product normalisation ────────────────────────────────────────────
// The backend product shape differs from what the existing components expect.
// This adapter converts backend products → the UI shape so we don't have to
// touch every component.

const HTML_ENTITIES = { '&amp;': '&', '&nbsp;': ' ', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'" }

/**
 * Cleans scraped description text:
 *  - decodes common HTML entities
 *  - normalises non-breaking spaces and CJK "一" used as a dash
 *  - collapses runaway whitespace/blank lines
 *  - dedupes consecutive identical paragraphs
 *  - drops a trailing paragraph fragment that ends mid-sentence
 */
function cleanDescription(raw) {
  if (!raw) return ''
  let text = String(raw).replace(/&(?:amp|nbsp|lt|gt|quot|#39);/g, (m) => HTML_ENTITIES[m] ?? m)
  text = text
    .replace(/\u00a0/g, ' ')
    .replace(/\s+一\s+/g, ' — ')
    .replace(/[ \t]+/g, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()

  const paras = text.split('\n\n')
  const deduped = paras.filter((p, i) => p.trim() !== (paras[i - 1] || '').trim())

  // If the last block is a cut-off fragment (no terminal punctuation and not a
  // table), trim it back to the last complete sentence — drop it if none exists.
  // ';' and ':' are valid endings for scraped bullet lists and labels.
  const last = deduped[deduped.length - 1]?.trim() || ''
  if (last && !/[.!?…;:|]\s*$/.test(last) && !/^\|.*\|$/m.test(last)) {
    const trimmed = last.replace(/[^.!?…]*$/, '').trim()
    if (trimmed) deduped[deduped.length - 1] = trimmed
    else deduped.pop()
  }

  return deduped.join('\n\n')
}

function normaliseProduct(p, segment) {
  const price = typeof p.price === 'string' ? parseFloat(p.price) : (p.price || 0)
  const categoryRaw = (Array.isArray(p.categories) ? p.categories[0] : '') || ''
  const category = categoryRaw.replace(/^Category:\s*/i, '')

  const base = {
    id: p.id,
    name: p.name || 'Untitled',
    description: cleanDescription(p.description),
    image: p.image || (Array.isArray(p.images) && p.images[0]) || null,
    category,
    stock_quantity: typeof p.stock_quantity === 'string' ? parseInt(p.stock_quantity, 10) : (p.stock_quantity || 0),
    channel: segment
  }

  if (segment === 'wholesale') {
    // Wholesale has its own calculation: the base price is discounted
    // (wholesale margin) and further discounted in volume tiers.
    const moq = 10
    const tiers = [
      { minQty: moq, factor: 0.85 },       // 15% below retail base
      { minQty: moq * 5, factor: 0.80 },   // 20% below retail base
      { minQty: moq * 10, factor: 0.75 }   // 25% below retail base
    ]
    const pricing = tiers.map((t) => ({
      minQty: t.minQty,
      price: Math.round(price * t.factor * 100) / 100
    }))

    return {
      ...base,
      price: pricing[0].price,
      currency: 'EUR',
      moq,
      moqUnit: 'units',
      pricing
    }
  }

  // retail — list price exactly as supplied by the backend
  return {
    ...base,
    price,
    currency: 'EUR',
    rating: null
  }
}

// ── Products ─────────────────────────────────────────────────────────

/**
 * Fetch products for the selected segment (retail or wholesale).
 * Both segments pull from the same live backend pool since the backend
 * has a single "main-website" channel.
 */
export const fetchProducts = async (segment, { category = null, search = '', page = 1, limit = 20 } = {}) => {
  if (segment !== 'retail' && segment !== 'wholesale') {
    throw new Error(`Unknown segment: ${segment}`)
  }

  const json = await request('/products', {
    page,
    limit,
    status: 'active',
    ...(search.trim() ? { search: search.trim() } : {})
  })

  if (!json.success) throw new Error(json.message || 'Failed to load products')

  let products = (json.data?.products || []).map((p) => normaliseProduct(p, segment))

  // Client-side category filter (uses the normalised name string)
  if (category) {
    products = products.filter((p) => p.category === category)
  }

  return {
    success: true,
    data: {
      products,
      pagination: json.data?.pagination || {
        page,
        limit,
        total: products.length,
        pages: Math.ceil(products.length / limit) || 1
      }
    }
  }
}

// ── Categories ───────────────────────────────────────────────────────

/**
 * Fetch categories for the selected segment.
 * Returns the same shape as the mock: { id, name, slug, description? }
 */
export const fetchCategories = async (segment) => {
  if (segment !== 'retail' && segment !== 'wholesale') {
    throw new Error(`Unknown segment: ${segment}`)
  }

  // Fetch all pages of categories (backend paginates at 50)
  let allCategories = []
  let page = 1
  let pages = 1

  do {
    const json = await request('/categories', { limit: 50, page })
    if (!json.success) break
    const cats = json.data?.categories || []
    allCategories = allCategories.concat(cats)
    pages = json.data?.pagination?.pages || 1
    page++
  } while (page <= pages)

  const data = allCategories.map((c) => ({
    id: c.id,
    name: c.name,
    slug: c.slug,
    description: c.description || ''
  }))

  return { success: true, data }
}

// ── Notifications ────────────────────────────────────────────────────

/**
 * Send an order/inquiry/contact message to the admin's WhatsApp via the
 * backend notification gateway (POST /api/notify).
 *
 * Returns { delivered: true }  — admin's WhatsApp received the message
 *         { delivered: false } — backend accepted it but no gateway is
 *                                configured yet, or the request failed.
 * Never throws — callers should fall back to a wa.me deep link when
 * delivered is false.
 */
export const submitInquiry = async (payload) => {
  const base = API_BASE_URL.startsWith('http') ? API_BASE_URL : window.location.origin
  try {
    const res = await fetch(new URL(`${API_BASE_URL}/notify`, base).toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(API_KEY ? { 'x-api-key': API_KEY } : {})
      },
      body: JSON.stringify(payload)
    })
    const json = await res.json().catch(() => ({}))
    return { delivered: res.ok && json.delivered === true }
  } catch {
    return { delivered: false }
  }
}
